import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { T, TF } from "react-translator-component";
import { Box, Button, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { colors } from "../../../common/colors";
import { imageAcceptedExtension, validateImage } from "../../../common/images";
import { convertHeicToJpeg, getFileExtenction } from "../../../common/files";
import { setErrors, setItem } from "../../redux/createOpinionReducer";
import { useAppDispatch } from "../../../redux/store";
import apiServices from "../../services/apiServices";
import { PostImageItem } from "../../util/createPost";
import Section from "../editors/Section";
import { styles as inputStyles } from "../editors/Styles";
import SelectSampleImages from "./SelectSampleImages";

const MAX_NUMBER_IMAGE_COUNT = 5;
const defaultImage = require("../../../assets/images/default.png");

export default function SelectImages() {
  const dispatch = useAppDispatch();

  const [images, setImages] = useState<PostImageItem[]>([]);
  const [removedImages, setRemovedImage] = useState<PostImageItem[]>([]);
  const [sampleImageList, setSampleImageList] = useState<any[]>([]);
  const post = useSelector((state: any) => state.createOpinion.values);
  const error = useSelector((state: any) => state.createOpinion.errors.images);

  const handleSelectLibraryImages = (event: any) => {
    if (!event.target.files) {
      console.error("ファイルを取得できませんでした。", event)
      return
    }

    const selectedFiles: any[] = Array.from(event.target.files);
    if (selectedFiles.length === 0) {
      console.error("ファイルがゼロ件です。");
      return
    }

    // 入力チェック
    let msg = ""
    const totalFiles = images.length + selectedFiles.length;
    if (totalFiles > MAX_NUMBER_IMAGE_COUNT) {
      msg = TF(T("error_image_count"), MAX_NUMBER_IMAGE_COUNT);
      selectedFiles.splice(MAX_NUMBER_IMAGE_COUNT - images.length);
    }

    let errMsg = ""
    for (let file of selectedFiles) {
      errMsg = validateImage(file)
      if (errMsg) {
        break
      }
    }

    if (errMsg) {
      dispatch(setErrors({ images: errMsg }));
    } else {
      dispatch(setErrors({ images: msg }));

      // 選択されたファイルをセット
      const newImages = selectedFiles.map((file: any) => {
        if (getFileExtenction(file) === "heic") {
          return new Promise(async (resolve, reject) => {
            convertHeicToJpeg(file)
              .then((f: File) => resolve({
                file: f,
                _destroy: "0",
              }))
              .catch((e: any) => reject(e))
          })
        } else {
          let reader = new FileReader();
          return new Promise((resolve) => {
            reader.onload = (e: any) => {
              resolve({
                file: file,
                _destroy: "0",
              });
            };
            reader.readAsDataURL(file);
          });
        }
      });

      Promise.all(newImages).then((loadedImages) => {
        setImages((prevImages: any) => [...prevImages, ...loadedImages]);
      }).catch((e: any) => console.error(e));
    }
  };

  const handleChangeSampleImages = (sampleImages: PostImageItem[]) => {
    const keep = sampleImages.length === 0
      ? []
      : images.filter((img: PostImageItem) => !img.sample_image_id || sampleImages.some((sample: PostImageItem) => img.sample_image_id === sample.sample_image_id))
    const additions = images.length === 0
      ? sampleImages
      : sampleImages.filter((sample: PostImageItem) => images.every((img: PostImageItem) => img.sample_image_id !== sample.sample_image_id))
    const deleted = images.filter((img: PostImageItem) =>
      img.sample_image_id && (sampleImages.length === 0 || sampleImages.every((sample: PostImageItem) => img.sample_image_id !== sample.sample_image_id))
    )

    const totalFiles = keep.length + additions.length - deleted.length;
    if (totalFiles > MAX_NUMBER_IMAGE_COUNT) {
      dispatch(setErrors({ images: TF(T("error_image_count"), MAX_NUMBER_IMAGE_COUNT)}));
      additions.splice(MAX_NUMBER_IMAGE_COUNT - keep.length + deleted.length);
    }
    saveRemoveImages(deleted);
    setImages([
      ...keep,
      ...additions.map((sample: PostImageItem) => {
        return { file: sample.file, sample_image_id: sample.sample_image_id, _destroy: "0" }
      })
    ]);
  }

  const removeImage = (img: PostImageItem, index: number) => {
    dispatch(setErrors({ images: "" }));
    saveRemoveImages([img]);
    setImages((prev: PostImageItem[]) => prev.filter((_: PostImageItem, i: number) => i !== index));
  };

  const saveRemoveImages = (imgList: PostImageItem[]) => {
    const list = imgList
      .filter((img: PostImageItem) => img.id)
      .map((img: PostImageItem) => ({ ...img, _destroy: "1" }));
    if (list.length > 0) {
      const _removedImages = [...removedImages, ...list];
      setRemovedImage(_removedImages);
    }
  };

  useEffect(() => {
    const filesToUpload = images
      .filter((img: PostImageItem) => img.file !== undefined)
      .map((img: PostImageItem) => (
        Boolean(img.sample_image_id) || Boolean(img.id)
          ? {id: img.id, sample_image_id: img.sample_image_id, _destroy: img._destroy} // 画像ファイルを送る必要は無いので、画像ファイルは除く
          : img
      ));
    const _removedImages = removedImages.map((img: PostImageItem) => ({id: img.id, _destroy: img._destroy}));
    dispatch(setItem({key: "images", value: [...filesToUpload, ..._removedImages], error: error}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images, removedImages]);

  useEffect(() => {
    if (!post?.id) return

    post.images.forEach((image: any) => {
      apiServices.getPostImage(post.id, image.id).then(blob => {
        const file = new File([blob], image.name, { type: blob.type });
        setImages((prevImages: any) =>
          [
            ...prevImages,
            { id: image.id, file: file, sample_image_id: image.sample_image_id, _destroy: "0" }
          ].sort((a: any, b: any) => a.id - b.id)
        );
      }).catch(() => {
        setImages((prevImages: any) =>
          [
            ...prevImages,
            { id: image.id, sample_image_id: image.sample_image_id, _destroy: "0" }
          ].sort((a: any, b:any) => a.id - b.id)
        );
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const getSampleImageList = async () => {
      await apiServices.getSampleImageList().then((res: any) => {
        setSampleImageList(res.samples);
      }).catch((error: any) => {
        console.error(error);
      })
    }
    getSampleImageList();
  }, []);

  const sampleImageModal = useMemo(() => {
    return (
      sampleImageList.length > 0 && (
        <SelectSampleImages
          sampleImages={sampleImageList}
          attachedImages={images}
          onChange={handleChangeSampleImages}
          limit={MAX_NUMBER_IMAGE_COUNT}
          />
      )
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sampleImageList, images])

  return (
    <Section
      title={
        <>
          {T("upload_images")}
          <br />
          {`${T("upload_images_notice")} ${T("optional")}`}
        </>
      }
      body={
        <>
          {error && <Typography style={inputStyles.errorText}>{ error }</Typography>}
          <Box>
            <div style={{ display: "flex", flexWrap: 'wrap', width: '100%', gap: '10px', padding: '0 20px', marginTop: '15px' }}>
              {images.map((item: PostImageItem, index: number) => (
                <div
                  key={index}
                  style={{
                    width: '18%',
                    minWidth: '150px',
                  }}
                >
                  <div
                    key={index}
                    style={{
                      position: 'relative' as 'relative',
                      bottom: 0,
                      left: 0,
                      width: '100%'
                    }}
                  >
                    {/* 画像を取得できない場合は削除マークを既定の位置に表示するためダミー画像を透明にして表示 */}
                    <img
                      style={{ width: '100%', opacity: item.file ? 1 : 0 }}
                      src={item.file ? URL.createObjectURL(item.file) : defaultImage}
                      alt=""
                    />
                    <DeleteIcon
                      style={{ top: -30, position: "relative" }}
                      onClick={() => removeImage(item, index)}
                      color={"warning"}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div style={{textAlign: "center"}}>
              {images.length < MAX_NUMBER_IMAGE_COUNT && (
                <>
                  <Button
                    color={"secondary"}
                    sx={{
                      width: 160,
                      borderRadius: 20,
                      borderWidth: 2,
                      fontFamily: "mplus-1c-regular",
                      color: colors.farm_black,
                      backgroundColor: colors.farm_white,
                      "&:hover": {
                        backgroundColor: colors.farm_white,
                      },
                    }}
                    component="label"
                    variant="outlined"
                  >
                    {T("select_libraries")}
                    <input accept={imageAcceptedExtension} type="file" hidden multiple onChange={handleSelectLibraryImages} />
                  </Button>
                  <div>
                    {sampleImageModal}
                  </div>
                </>
              )}
            </div>
          </Box>
        </>
      }
    />
  )
}
